/**
 * Regras do popup de perfil (docs/spec/friends.md, "Perfil"), sem React nem store: as etiquetas de
 * "Gosta de jogar", a relação que o pé mostra, quantos textos do meu perfil estão escritos e o
 * endereço da capa.
 */
import { serverUrl } from '../serverUrl.js';
import { deskFileRefId, type ContactList, type UserProfile } from '../shared.js';

/** Um pedaço maior que isto não é etiqueta: o texto inteiro fica corrido, como a pessoa escreveu. */
const CHIP_MAX = 30;

/**
 * "D&D 5e, Tormenta20; Call of Cthulhu" vira três etiquetas: separa por vírgula, ponto e vírgula e
 * linha. `null` quando não é uma lista (vazio, ou um pedaço comprido demais).
 */
export function preferenceChips(text: string): string[] | null {
  const parts = text
    .split(/[,;\n]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0 || parts.some((part) => part.length > CHIP_MAX)) return null;
  return parts;
}

/**
 * A relação que o pé do perfil mostra. `sent` e `received` são o pedido de amizade enviado e o
 * recebido; `pending` é o pedido quando não dá para saber de quem foi.
 */
export type ProfileRelation = 'me' | 'none' | 'sent' | 'received' | 'pending' | 'friend' | 'hidden' | 'blocked';

type Contacts = Pick<ContactList, 'friends' | 'hidden' | 'blocked' | 'outgoing' | 'incoming'>;

/**
 * A relação com a pessoa do perfil. A lista de contatos manda, porque muda na hora (o pedido que saiu
 * do próprio perfil já aparece), e o perfil só traz a relação de quando abriu. Sem a lista, vale a
 * do perfil.
 */
export function profileRelation(profile: Pick<UserProfile, 'user' | 'relation' | 'isMe'>, contacts: Contacts | null): ProfileRelation {
  if (profile.isMe) return 'me';
  if (contacts) {
    const has = (list: Contacts['friends']) => list.some((contact) => contact.user.id === profile.user.id);
    if (has(contacts.blocked)) return 'blocked';
    if (has(contacts.friends)) return 'friend';
    if (has(contacts.hidden)) return 'hidden';
    if (has(contacts.outgoing)) return 'sent';
    if (has(contacts.incoming)) return 'received';
    return 'none';
  }
  return profile.relation ?? 'none';
}

/**
 * O endereço da capa: o arquivo do Desk Cloud vai pela rota pública da pessoa, com o id do arquivo
 * como versão (só fura o cache quando a capa muda); o endereço da web vai como está. null sem capa.
 */
export function profileCoverSrc(userId: string, cover: string | null): string | null {
  if (!cover) return null;
  const fileId = deskFileRefId(cover);
  return serverUrl(fileId ? `/api/users/${encodeURIComponent(userId)}/cover?v=${encodeURIComponent(fileId)}` : cover);
}

/** Quantos dos três textos do perfil têm algo escrito. */
export function filledCount(profile: Pick<UserProfile, 'bio' | 'rpgPreferences' | 'timePreferences'>): number {
  return [profile.bio, profile.rpgPreferences, profile.timePreferences].filter((text) => text.trim() !== '').length;
}
